// @flow
import api from 'api'
import Spinner from 'components/Spinner'
import ModalAddTeamUser from 'components/Team/ModalAddTeamUser'
import {
  invalidateLoadUsers,
  useLoadUsersLabels,
} from 'queries/users/useLoadUsers'
import * as React from 'react'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import CopyButton from 'ui/atoms/CopyButton'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import Tooltip from 'ui/atoms/Tooltip'
import Icons from 'ui/Icons'
import SelectField from 'ui/molecules/SelectField'
import Page from 'ui/organisms/Page'
import { ZentakeContext } from 'utils/ZentakeContext'

type Provider = {
  id: number,
  external_id: string,
  first_name: string,
  last_name: string,
  npi: string,
  is_active: boolean,
}

let interval = null

function ElationSettings(): React.Node {
  const { data: users } = useLoadUsersLabels()
  const { teamDetails } = React.useContext(ZentakeContext)

  const [addUserModalOpen, setAddUserModalOpen] = React.useState(false)

  const [completed, setCompleted] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [loadingProviders, setLoadingProviders] = React.useState(false)
  const [providers, setProviders] = React.useState<Array<Provider>>([])
  const [canSyncProviderAvailability, setCanSyncProviderAvailability] =
    React.useState<{
      can_sync_again: boolean,
      in_minutes: number,
    }>({
      can_sync_again: false,
      in_minutes: 0,
    })

  React.useEffect(() => {
    const checkCanSyncProviderAvailability = async () => {
      try {
        const response = await api.canSyncAgainProviderAvailability()
        setCanSyncProviderAvailability(
          response ?? { can_sync_again: false, in_minutes: 0 }
        )
      } catch (e) {
        toast.error('Something went wrong')
        throw e
      }
    }
    checkCanSyncProviderAvailability()
  }, [])

  const fetchSync = React.useCallback(async () => {
    const response = await api.getMyElationSyncs()

    if (response.status === 'running') {
      setLoading(true)

      if (!interval) {
        interval = setInterval(fetchSync, 1000)
      }
      return
    }

    setLoading(false)

    if (interval) {
      clearInterval(interval)
    }

    if (response.status === 'completed') {
      setCompleted(true)
    }
  }, [])

  const fetchProviders = React.useCallback(async () => {
    setLoadingProviders(true)
    try {
      const response = await api.getElationProviders()
      setProviders(response)
    } catch {
      toast.error('Something went wrong')
    }
    setLoadingProviders(false)
  }, [])

  const onSyncWithElation = React.useCallback(async () => {
    setLoading(true)
    await api.createElationSync()
    await fetchSync()
  }, [fetchSync])

  const onSyncProviderAvailability = React.useCallback(async () => {
    setLoading(true)
    try {
      await api.syncProviderAvailability()
      setCanSyncProviderAvailability({
        can_sync_again: false,
        in_minutes: 10,
      })
      await fetchProviders()
      toast.success(
        'Calendar sync started. It may take a few minutes to complete.'
      )
    } catch (e) {
      toast.error('Something went wrong')
      throw e
    }
    setLoading(false)
  }, [fetchProviders])

  React.useEffect(() => {
    interval = null

    fetchSync()
    fetchProviders()

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [fetchProviders, fetchSync])

  const getProviderOptions = React.useCallback(
    (userId) => {
      const providersAssociatedToOtherUsers = users
        ? users
            .filter((user) => user.provider && user.id !== userId)
            .map((user) => user.provider.toString())
        : []

      const options = providers
        .filter(
          (provider) =>
            !providersAssociatedToOtherUsers.includes(provider.id.toString())
        )
        .map((provider) => ({
          label: `${provider.first_name} ${provider.last_name} <${provider.external_id}>`,
          value: provider.id.toString(),
        }))

      return [
        {
          label: 'No provider',
          value: '',
        },
        ...options,
      ]
    },
    [providers, users]
  )

  const getOnChangeProvider = React.useCallback(
    (userId: number) => async (event) => {
      setLoading(true)
      event.persist()

      const data = {
        provider: event.target.value,
      }

      try {
        await api.updateUser(userId, data)
        toast.success('User updated')
      } catch {
        toast.error('Something went wrong')
      } finally {
        invalidateLoadUsers()
      }
      setLoading(false)
    },
    []
  )

  const sortedUsers = React.useMemo(
    () =>
      users
        ? users.sort((a, b) => {
            if (a.first_name < b.first_name) {
              return -1
            }
            if (a.first_name > b.first_name) {
              return 1
            }
            return 0
          })
        : [],
    [users]
  )

  const renderTable = React.useCallback(() => {
    if (loadingProviders) {
      return (
        <Flex justify="center">
          <Spinner color="onLight" size={128} />
        </Flex>
      )
    }

    return (
      <Table>
        <Table.Head columns={['Zentake Users', 'Elation Providers', '']} />
        <Table.Body>
          {sortedUsers.map((user) => (
            <Table.Row key={user.id}>
              <Table.Column>{`${user.first_name} ${user.last_name} <${user.email}>`}</Table.Column>
              <Table.Column>
                <SelectField
                  disabled={loading}
                  onChange={getOnChangeProvider(user.id)}
                  options={getProviderOptions(user.id)}
                  value={user.provider?.toString() ?? ''}
                />
              </Table.Column>
              <Table.Column>
                {user.provider && (
                  <Tooltip label="Copy provider ID">
                    <CopyButton
                      text={
                        providers?.find(
                          (provider) => provider.id === user.provider
                        )?.external_id ?? ''
                      }
                    />
                  </Tooltip>
                )}
              </Table.Column>
            </Table.Row>
          ))}
          <Table.Row $muted>
            <Table.Column>
              <Button
                $noOutline
                $plain
                $primary
                onClick={() => setAddUserModalOpen(true)}
              >
                Add New User
              </Button>
            </Table.Column>
            <Table.Column>
              <SelectField disabled options={[]} />
            </Table.Column>
            <Table.Column />
          </Table.Row>
        </Table.Body>
      </Table>
    )
  }, [
    getOnChangeProvider,
    getProviderOptions,
    loading,
    loadingProviders,
    providers,
    sortedUsers,
  ])

  return (
    <Page title="Elation Integration Settings">
      <Space>
        <Text>Start Elation Sync</Text>
        <Flex $horizontalSpacing="small">
          <Button
            $primary
            disabled={completed}
            icon={<Icons.ArrowPath inheritColor />}
            loading={loading}
            onClick={onSyncWithElation}
          >
            {completed ? 'Elation Synced' : 'Sync Elation'}
          </Button>
          {teamDetails?.show_sync_calendar && (
            <Tooltip
              label={
                canSyncProviderAvailability?.in_minutes
                  ? `You can sync again in ${canSyncProviderAvailability?.in_minutes} minutes`
                  : 'Sync appointments, provider availability, and more from Elation to Zentake. This may take a few minutes to complete.'
              }
            >
              <Button
                $primary
                disabled={!canSyncProviderAvailability?.can_sync_again}
                loading={loading}
                onClick={onSyncProviderAvailability}
              >
                Sync Calendar
              </Button>
            </Tooltip>
          )}
        </Flex>
        <div>
          <Text $xlarge>Map Providers</Text>
          <Text>Map each Elation Provider(s) with a Zentake User</Text>
        </div>
        {renderTable()}
      </Space>
      {addUserModalOpen && (
        <ModalAddTeamUser onClose={() => setAddUserModalOpen(false)} />
      )}
    </Page>
  )
}

export default ElationSettings
